import Keycloak from 'keycloak-js'
import type { KeycloakTokenParsed } from 'keycloak-js'
import { appConfig } from '@/config/appConfig'
import { logger } from '@/services/logger'

const LOGOUT_BROADCAST_CHANNEL = 'talora-auth'
const TOKEN_REFRESH_SECONDS = 30

let keycloakInstance: Keycloak | null = null
let initPromise: Promise<void> | null = null

// Fake token used in mock mode so the UI renders a believable operator identity
// without any Keycloak round-trip. Mirrors the real tokenParsed shape (preferred_username,
// email, realm_access.roles, resource_access[client].roles).
const MOCK_TOKEN_PARSED: KeycloakTokenParsed = {
  preferred_username: 'ops.admin',
  email: 'ops.admin@dextradata.com',
  realm_access: { roles: ['ADMIN'] },
  resource_access: {
    provisioningservice: { roles: ['provisioning-admin'] },
  },
}

let mockTokenParsed: KeycloakTokenParsed | null = null

export async function initAuth(): Promise<void> {
  if (appConfig.useMocks()) {
    mockTokenParsed = MOCK_TOKEN_PARSED
    return
  }
  if (keycloakInstance) return
  if (initPromise) return initPromise

  initPromise = doInitKeycloak()
  return initPromise
}

async function doInitKeycloak(): Promise<void> {
  const kc = new Keycloak({
    url: appConfig.keycloakUrl(),
    realm: appConfig.keycloakRealm(),
    clientId: appConfig.keycloakClientId(),
  })

  kc.onTokenExpired = () => {
    logger.debug('Token expired, refreshing...')
    kc.updateToken(TOKEN_REFRESH_SECONDS).catch(() => {
      logger.warn('Token refresh failed, redirecting to login')
      kc.login()
    })
  }

  const authenticated = await kc.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    // The general-admin-frontend Keycloak client enforces PKCE (S256); the adapter
    // only sends a code_challenge when pkceMethod is set, so this must stay in lockstep
    // with the client's pkce.code.challenge.method attribute or login is rejected.
    pkceMethod: 'S256',
    // Surface keycloak-js' own callback/login decisions to the console. The app
    // logger is quiet in prod, which previously hid why a login loop occurred.
    enableLogging: true,
  })

  if (!authenticated) {
    throw new Error('Keycloak authentication failed')
  }

  stripKeycloakCallbackHash()

  keycloakInstance = kc
  logger.info('Keycloak initialized, operator authenticated')
}

function tokenParsed(): KeycloakTokenParsed | null {
  if (appConfig.useMocks()) return mockTokenParsed
  return keycloakInstance?.tokenParsed ?? null
}

function stripKeycloakCallbackHash(): void {
  if (typeof window === 'undefined') return
  if (!window.location.hash) return
  if (!/[#&](state|code|session_state|iss)=/.test(window.location.hash)) return
  const cleanUrl = window.location.pathname + window.location.search
  window.history.replaceState(null, '', cleanUrl)
}

export function getAuthToken(): string | null {
  if (appConfig.useMocks()) return 'mock-token'
  return keycloakInstance?.token ?? null
}

/**
 * Whether the operator has an authenticated session. initAuth() runs once in
 * main.ts before the app mounts, so the router guard only needs to read this
 * (never re-initialize Keycloak — re-init during navigation caused a login loop).
 */
export function isAuthenticated(): boolean {
  if (appConfig.useMocks()) return true
  return keycloakInstance?.authenticated ?? false
}

export function getUsername(): string {
  return (tokenParsed()?.preferred_username as string | undefined) ?? ''
}

export function getUserEmail(): string {
  return (tokenParsed()?.email as string | undefined) ?? ''
}

export function getRealmRoles(): string[] {
  return tokenParsed()?.realm_access?.roles ?? []
}

export function getResourceRoles(client: string): string[] {
  return tokenParsed()?.resource_access?.[client]?.roles ?? []
}

export function performLogout(): void {
  logger.info('Operator initiated logout')

  if (appConfig.useMocks()) {
    clearClientData()
    window.location.replace(window.location.origin)
    return
  }

  if (!keycloakInstance) {
    logger.error('keycloakInstance is null at logout time, forcing redirect')
    clearClientData()
    window.location.replace(window.location.origin)
    return
  }

  const redirectUri = window.location.origin

  try {
    keycloakInstance.logout({ redirectUri })
  } catch (err) {
    logger.error('Keycloak logout failed, forcing redirect', err)
    clearClientData()
    window.location.replace(redirectUri)
  }
}

function clearClientData(): void {
  try {
    localStorage.clear()
    sessionStorage.clear()
  } catch {
    /* ignore */
  }

  try {
    document.cookie.split(';').forEach((c) => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`)
    })
  } catch {
    /* ignore */
  }

  keycloakInstance = null
  initPromise = null
  mockTokenParsed = null
}

export function setupCrossTabLogoutListener(): void {
  try {
    const bc = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL)
    bc.onmessage = (event) => {
      if (event.data?.type === 'logout') {
        logger.info('Logout broadcast received from another tab')
        clearClientData()
        window.location.replace(window.location.origin)
      }
    }
  } catch {
    window.addEventListener('storage', (e) => {
      if (e.key === 'TALORA_LOGOUT_EVENT' && e.newValue) {
        logger.info('Logout storage event received from another tab')
        clearClientData()
        window.location.replace(window.location.origin)
      }
    })
  }
}
